import React, { useEffect, useRef, useState, memo } from 'react';
import {
  Code,
  Cloud,
  Shield,
  Award,
  Server,
  ShieldCheck,
  Network,
  CloudCog,
  BrainCircuit,
  ArrowRight
} from 'lucide-react';
import { servicesData } from '../data/mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const iconMap = {
  Code,
  Cloud,
  Shield,
  Award,
  Server,
  ShieldCheck,
  Network,
  CloudCog,
  BrainCircuit
};

// Memoized Card Component with individual IntersectionObserver
const ServiceCard = memo(({ service, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  const IconComponent = iconMap[service.icon];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Add a small delay based on index for staggered effect, but handled individually
          setTimeout(() => {
            setIsVisible(true);
          }, index * 50);
          if (cardRef.current) observer.unobserve(cardRef.current);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const currentCardRef = cardRef.current;

    if (currentCardRef) {
      observer.observe(currentCardRef);
    }

    return () => {
      if (currentCardRef) {
        observer.unobserve(currentCardRef);
      }
    };
  }, [index]);

  const handleScrollToContact = () => {
    const element = document.querySelector('#contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div
      ref={cardRef}
      className={`transform transition-all duration-700 ease-out ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
      style={{ willChange: 'transform, opacity' }}
    >
      <Card className="h-full border-0 shadow-lg shadow-blue-900/5 hover:shadow-2xl hover:shadow-blue-900/20 transition-all duration-300 group overflow-hidden bg-white">
        {/* Image Header */}
        <div className="relative h-48 overflow-hidden bg-gray-100">
          <img
            src={service.image}
            alt={service.title}
            loading="lazy"
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500 will-change-transform"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[rgb(37,159,235)]/90 to-transparent"></div>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
              {IconComponent && (
                <IconComponent className="w-6 h-6 text-blue-600" />
              )}
            </div>
          </div>
        </div>

        <CardHeader>
          <CardTitle className="text-xl mb-2 group-hover:text-blue-600 transition-colors">
            {service.title}
          </CardTitle>
          <CardDescription className="text-base leading-relaxed">
            {service.description}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {service.features.map((feature, idx) => (
              <Badge
                key={idx}
                variant="secondary"
                className="bg-blue-50 text-blue-700 hover:bg-blue-100"
              >
                {feature}
              </Badge>
            ))}
          </div>

          <Button
            variant="ghost"
            className="w-full group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors"
            onClick={handleScrollToContact}
          >
            Más información
            <ArrowRight
              className="ml-2 w-4 h-4 transform transition-transform group-hover:translate-x-1"
            />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
});

ServiceCard.displayName = 'ServiceCard';

const Services = () => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const currentRef = headerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsHeaderVisible(true);
          if (currentRef) observer.unobserve(currentRef);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <section id="services" className="py-14 md:py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-cyan-100/30 rounded-full blur-3xl transform translate-z-0"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl transform translate-z-0"></div>
      </div>

      <div className="container mx-auto px-5 sm:px-8 relative z-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transform transition-all duration-1000 ${isHeaderVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6">
            <span className="text-gray-900">Nuestros </span>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Servicios</span>
          </h2>
          <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mb-6 md:mb-8"></div>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Soluciones tecnológicas integrales diseñadas para impulsar tu negocio al siguiente nivel
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
          {servicesData.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
